import {
  CirclePlay,
  CircleStop,
  Disc3,
  Headphones,
  History,
  Info,
  ListMusic,
  LoaderCircle,
  Pause,
  Play,
  Radio,
  RadioTower,
  SkipForward,
  Volume1,
  Volume2,
  UsersRound,
  VolumeX
} from 'lucide-react';
import Hls from 'hls.js';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from 'react';
import type { NowPlayingResponse, StationLibraryStats } from './types';

const DEFAULT_VOLUME = 1;
const METADATA_STALE_AFTER_MS = 1_500;
const FALLBACK_CHECK_MS = 750;
const VISUALIZER_BARS = 61;
const VISUALIZER_PALETTE = [
  '#ffff00', // Mitte: Gelb
  '#0000ff', // danach: Blau
  '#4caf50', // danach: Grün
  '#ff0000', // danach: Rot
  '#ff0099', // danach: Pink
  '#107c10'  // danach: Dunkelgrün
] as const;

function visualizerBarColor(index: number): string {
  const center = (VISUALIZER_BARS - 1) / 2;
  const distanceFromCenter = Math.round(Math.abs(index - center));

  // Jede Distanzstufe verwendet die nächste Farbe. Links und rechts sind
  // dadurch exakt gespiegelt: Mitte Gelb, dann Blau, Grün, Rot, Pink,
  // Dunkelgrün und anschließend beginnt die Reihenfolge erneut.
  return VISUALIZER_PALETTE[distanceFromCenter % VISUALIZER_PALETTE.length];
}

interface PlaybackAnchor {
  trackKey: string;
  duration: number;
  startedAt: number | null;
  elapsedAtSync: number;
  serverTimeAtSyncMs: number;
  syncedAtMs: number;
}

function monotonicNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${minutes}:${remaining.toString().padStart(2, '0')}`;
}

function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('de-DE').format(value)
    : '–';
}

function formatTimestamp(timestamp: number | null): string {
  if (!timestamp) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp * 1000));
}

function compactTitleClass(title: string | null | undefined): string {
  const length = (title || '').trim().length;
  if (length >= 36) return 'song-title-size-xs';
  if (length >= 24) return 'song-title-size-sm';
  if (length >= 10) return 'song-title-size-md';
  return 'song-title-size-lg';
}

function versionToken(...values: Array<string | number | null | undefined>): string {
  return encodeURIComponent(values.filter((value) => value !== null && value !== undefined).join('|'));
}

function useAutoFitTitle(text: string) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    const title = titleRef.current;
    const container = title?.parentElement;
    if (!title || !container) return;

    let frame = 0;

    const fit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const width = container.clientWidth;
        const viewportHeight = window.innerHeight;
        const maximum = Math.min(54, Math.max(34, width * 0.112));
        const minimum = width < 430 ? 23 : 26;
        const maximumHeight = Math.min(208, Math.max(132, viewportHeight * 0.22));

        let size = maximum;
        title.style.fontSize = `${size}px`;
        title.style.lineHeight = '1.18';
        title.style.letterSpacing = '-0.045em';

        while (
          size > minimum &&
          (title.scrollHeight > maximumHeight || title.scrollWidth > title.clientWidth + 1)
        ) {
          size -= 1;
          title.style.fontSize = `${size}px`;
        }

        title.dataset.fitted = 'true';
      });
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    window.addEventListener('resize', fit);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [text]);

  return titleRef;
}

export default function App() {
  const shellRef = useRef<HTMLElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const streamReadyRef = useRef(false);
  const initialLiveSeekDoneRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const visualizerRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const playbackAnchorRef = useRef<PlaybackAnchor | null>(null);
  const lastMetadataAtRef = useRef(0);
  const lastServerTimeRef = useRef(0);
  const lastRevisionRef = useRef(0);
  const fetchInFlightRef = useRef<Promise<void> | null>(null);

  const [nowPlaying, setNowPlaying] = useState<NowPlayingResponse | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [previousVolume, setPreviousVolume] = useState(DEFAULT_VOLUME);
  const [clock, setClock] = useState(() => monotonicNow());

  const currentTitle = nowPlaying?.nowPlaying.title || 'Titel wird geladen';
  const titleRef = useAutoFitTitle(currentTitle);

  const applyNowPlaying = useCallback((incoming: NowPlayingResponse) => {
    const syncTime = monotonicNow();

    if (
      incoming.serverTimeMs < lastServerTimeRef.current &&
      incoming.revision <= lastRevisionRef.current
    ) {
      return;
    }

    lastServerTimeRef.current = Math.max(lastServerTimeRef.current, incoming.serverTimeMs);
    lastRevisionRef.current = Math.max(lastRevisionRef.current, incoming.revision);
    lastMetadataAtRef.current = Date.now();

    const trackKey = incoming.nowPlaying.trackId || [
      incoming.nowPlaying.startedAt ?? 'unknown-start',
      incoming.nowPlaying.title,
      incoming.nowPlaying.artist
    ].join('|');

    const duration = Math.max(0, incoming.nowPlaying.duration || 0);
    const startedAt = incoming.nowPlaying.startedAt;

    // Die Laufzeit stammt ausschließlich aus den AzuraCast-Zeitdaten.
    // Eine kleine, plausible Übertragungszeit wird addiert, damit die Anzeige
    // bei Ankunft des Pakets nicht bereits hinter der Quelle liegt.
    const observedTransitMs = Date.now() - incoming.serverTimeMs;
    const transitMs = observedTransitMs >= 0 && observedTransitMs <= 5_000
      ? observedTransitMs
      : 0;
    const incomingServerNowMs = incoming.serverTimeMs + transitMs;

    const previousAnchor = playbackAnchorRef.current;
    const sameTrack = previousAnchor?.trackKey === trackKey;

    const previousServerNowMs = sameTrack && previousAnchor
      ? previousAnchor.serverTimeAtSyncMs + Math.max(0, syncTime - previousAnchor.syncedAtMs)
      : 0;

    // Für denselben Titel darf die Zeit niemals rückwärts laufen. Neue Pakete
    // aktualisieren Länge und Endzeit, aber nicht mit älteren Zeitständen.
    const stableServerNowMs = sameTrack
      ? Math.max(previousServerNowMs, incomingServerNowMs)
      : incomingServerNowMs;

    let elapsedAtSync = startedAt
      ? Math.max(0, stableServerNowMs / 1000 - startedAt)
      : Math.max(0, incoming.nowPlaying.elapsed || 0);

    if (duration > 0) elapsedAtSync = Math.min(duration, elapsedAtSync);

    playbackAnchorRef.current = {
      trackKey,
      duration,
      startedAt,
      elapsedAtSync,
      serverTimeAtSyncMs: stableServerNowMs,
      syncedAtMs: syncTime
    };

    setNowPlaying(incoming);
    setClock(syncTime);
  }, []);

  const fetchNowPlaying = useCallback(async () => {
    if (fetchInFlightRef.current) return fetchInFlightRef.current;

    const request = (async () => {
      try {
        const response = await fetch(`/api/now-playing?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        applyNowPlaying((await response.json()) as NowPlayingResponse);
      } catch (error) {
        console.error('Now-Playing konnte nicht geladen werden:', error);
      }
    })().finally(() => {
      fetchInFlightRef.current = null;
    });

    fetchInFlightRef.current = request;
    return request;
  }, [applyNowPlaying]);

  useEffect(() => {
    void fetchNowPlaying();

    const eventSource = new EventSource('/api/events');
    const handleLiveMetadata = (event: MessageEvent<string>) => {
      try {
        applyNowPlaying(JSON.parse(event.data) as NowPlayingResponse);
      } catch (error) {
        console.error('Live-Metadaten konnten nicht verarbeitet werden:', error);
      }
    };

    const handleLiveStats = (event: MessageEvent<string>) => {
      try {
        const library = JSON.parse(event.data) as StationLibraryStats;
        setNowPlaying((current) => current ? { ...current, library } : current);
      } catch (error) {
        console.error('Live-Statistiken konnten nicht verarbeitet werden:', error);
      }
    };

    eventSource.addEventListener('now-playing', handleLiveMetadata as EventListener);
    eventSource.addEventListener('library-stats', handleLiveStats as EventListener);
    eventSource.onopen = () => {
      if (Date.now() - lastMetadataAtRef.current > 1_500) void fetchNowPlaying();
    };

    const fallbackTimer = window.setInterval(() => {
      if (Date.now() - lastMetadataAtRef.current > METADATA_STALE_AFTER_MS) {
        void fetchNowPlaying();
      }
    }, FALLBACK_CHECK_MS);

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void fetchNowPlaying();
    };
    const refreshWhenOnline = () => void fetchNowPlaying();

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('online', refreshWhenOnline);

    return () => {
      eventSource.removeEventListener('now-playing', handleLiveMetadata as EventListener);
      eventSource.removeEventListener('library-stats', handleLiveStats as EventListener);
      eventSource.close();
      window.clearInterval(fallbackTimer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenOnline);
    };
  }, [applyNowPlaying, fetchNowPlaying]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(monotonicNow()), 200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const resetVisualizer = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const center = (VISUALIZER_BARS - 1) / 2;
    visualizerRefs.current.forEach((bar, index) => {
      if (!bar) return;
      const distance = Math.abs(index - center) / center;
      const idleLevel = 0.06 + (1 - distance) * 0.035;
      bar.style.setProperty('--level', idleLevel.toFixed(3));
      bar.style.setProperty('--glow', '0.16');
    });

    shellRef.current?.style.setProperty('--energy', '0.08');
    shellRef.current?.style.setProperty('--bass', '0.05');
    shellRef.current?.style.setProperty('--mid', '0.05');
    shellRef.current?.style.setProperty('--treble', '0.05');
  }, []);

  const startVisualizer = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    const center = (VISUALIZER_BARS - 1) / 2;

    const averageRange = (start: number, end: number) => {
      let total = 0;
      const safeEnd = Math.min(end, frequencyData.length);
      for (let index = start; index < safeEnd; index += 1) total += frequencyData[index];
      return safeEnd > start ? total / (safeEnd - start) / 255 : 0;
    };

    const render = () => {
      analyser.getByteFrequencyData(frequencyData);

      const usefulEnd = Math.min(frequencyData.length, 168);
      const fullSpectrumEnergy = averageRange(1, usefulEnd);
      let totalEnergy = 0;

      visualizerRefs.current.forEach((bar, index) => {
        if (!bar) return;

        // Beide Seiten verwenden exakt dasselbe Frequenzband. Dadurch breitet
        // sich der Analyzer geordnet und spiegelbildlich von der Mitte aus.
        const outwardPosition = Math.abs(index - center) / center;
        const highestBin = Math.max(8, usefulEnd - 8);
        const baseBin = Math.min(
          highestBin,
          Math.floor(2 + Math.pow(outwardPosition, 1.3) * (highestBin - 2))
        );

        let localTotal = 0;
        let localSamples = 0;
        for (let offset = -3; offset <= 3; offset += 1) {
          const bin = Math.max(1, Math.min(frequencyData.length - 1, baseBin + offset));
          localTotal += frequencyData[bin];
          localSamples += 1;
        }

        const localEnergy = localTotal / localSamples / 255;
        // Ein kleiner Anteil der Gesamtenergie sorgt dafür, dass der komplette
        // Visualizer genutzt wird, ohne die Frequenzcharakteristik zu verlieren.
        const combinedEnergy = localEnergy * 0.84 + fullSpectrumEnergy * 0.16;
        const shaped = Math.pow(combinedEnergy, 0.74);
        const level = Math.min(1, 0.07 + shaped * 0.93);

        totalEnergy += level;
        bar.style.setProperty('--level', level.toFixed(3));
      });

      const energy = totalEnergy / VISUALIZER_BARS;
      const bass = averageRange(1, 18);
      const mid = averageRange(18, 60);
      const treble = averageRange(60, 120);

      shellRef.current?.style.setProperty('--energy', energy.toFixed(3));
      shellRef.current?.style.setProperty('--bass', bass.toFixed(3));
      shellRef.current?.style.setProperty('--mid', mid.toFixed(3));
      shellRef.current?.style.setProperty('--treble', treble.toFixed(3));
      animationFrameRef.current = window.requestAnimationFrame(render);
    };

    render();
  }, []);

  const initialiseAudioAnalysis = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    const AudioContextConstructor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextConstructor) return;

    if (!audioContextRef.current) {
      const context = new AudioContextConstructor();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.68;
      analyser.minDecibels = -96;
      analyser.maxDecibels = -14;

      const source = context.createMediaElementSource(audio);
      source.connect(analyser);
      analyser.connect(context.destination);

      audioContextRef.current = context;
      analyserRef.current = analyser;
      mediaSourceRef.current = source;
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
  }, []);

  const forceNormalPlaybackRate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.defaultPlaybackRate = 1;
    audio.preservesPitch = true;

    if (Math.abs(audio.playbackRate - 1) > 0.001) {
      audio.playbackRate = 1;
    }
  }, []);

  useEffect(() => {
    const rateGuard = window.setInterval(forceNormalPlaybackRate, 500);
    return () => window.clearInterval(rateGuard);
  }, [forceNormalPlaybackRate]);

  const seekToStableLivePoint = useCallback((force = false) => {
    const audio = audioRef.current;
    const hls = hlsRef.current;
    if (!audio) return;

    forceNormalPlaybackRate();

    let target: number | null = null;
    const hlsTarget = hls?.liveSyncPosition;

    if (typeof hlsTarget === 'number' && Number.isFinite(hlsTarget)) {
      target = hlsTarget;
    } else if (audio.seekable.length > 0) {
      const rangeIndex = audio.seekable.length - 1;
      const start = audio.seekable.start(rangeIndex);
      const edge = audio.seekable.end(rangeIndex);
      if (Number.isFinite(start) && Number.isFinite(edge)) {
        // Bewusst einige Sekunden hinter der absoluten Live-Kante bleiben.
        // Das verhindert Nachladen, Catch-up und hörbare Geschwindigkeitssprünge.
        target = Math.max(start, edge - 8);
      }
    }

    if (target === null || !Number.isFinite(target)) return;

    const current = audio.currentTime;
    const lag = Number.isFinite(current) ? target - current : Number.POSITIVE_INFINITY;

    // Nur beim Start/Resume oder bei wirklich großer Abweichung springen.
    // Kleine Live-Schwankungen werden nie durch schnelleres Abspielen ausgeglichen.
    if (force || !Number.isFinite(current) || lag > 18 || lag < -4) {
      audio.currentTime = Math.max(0, target);
    }
  }, [forceNormalPlaybackRate]);

  const initialiseStream = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) throw new Error('Audio-Element ist nicht verfügbar.');
    if (streamReadyRef.current) return;

    hlsRef.current?.destroy();
    hlsRef.current = null;
    streamReadyRef.current = false;
    initialLiveSeekDoneRef.current = false;
    forceNormalPlaybackRate();

    const source = '/api/hls/manifest.m3u8';

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        liveSyncDurationCount: 4,
        liveMaxLatencyDurationCount: 12,
        maxLiveSyncPlaybackRate: 1,
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferHole: 0.5,
        manifestLoadingTimeOut: 12_000,
        levelLoadingTimeOut: 12_000,
        fragLoadingTimeOut: 18_000
      });

      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad(-1);
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        streamReadyRef.current = false;
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        setIsPlaying(false);
        setIsBuffering(false);
        resetVisualizer();
        setStreamError('Der Livestream wurde unterbrochen.');
      });

      hls.on(Hls.Events.LEVEL_LOADED, () => {
        forceNormalPlaybackRate();

        if (!initialLiveSeekDoneRef.current) {
          initialLiveSeekDoneRef.current = true;
          window.requestAnimationFrame(() => seekToStableLivePoint(true));
        }
      });

      await new Promise<void>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          reject(new Error('Das HLS-Manifest konnte nicht rechtzeitig geladen werden.'));
        }, 15_000);

        hls.once(Hls.Events.MANIFEST_PARSED, () => {
          window.clearTimeout(timeout);
          streamReadyRef.current = true;
          forceNormalPlaybackRate();
          resolve();
        });

        hls.once(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          window.clearTimeout(timeout);
          reject(new Error(`HLS-Fehler: ${data.details}`));
        });

        hls.attachMedia(audio);
        hls.loadSource(source);
      });
      return;
    }

    if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      audio.src = source;
      streamReadyRef.current = true;
      return;
    }

    throw new Error('Dieser Client unterstützt keine HLS-Wiedergabe.');
  }, [forceNormalPlaybackRate, resetVisualizer, seekToStableLivePoint]);

  useEffect(() => {
    resetVisualizer();

    const resync = () => {
      if (document.visibilityState === 'visible' && !audioRef.current?.paused) {
        seekToStableLivePoint(false);
      }
    };
    document.addEventListener('visibilitychange', resync);

    return () => {
      document.removeEventListener('visibilitychange', resync);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      void audioContextRef.current?.close();
    };
  }, [resetVisualizer, seekToStableLivePoint]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (audio.paused) {
        setStreamError(null);
        setIsBuffering(true);
        await initialiseStream();
        await initialiseAudioAnalysis();
        seekToStableLivePoint(true);
        forceNormalPlaybackRate();
        await audio.play();
      } else {
        audio.pause();
      }
    } catch (error) {
      console.error(error);
      setIsBuffering(false);
      resetVisualizer();
      setStreamError(
        error instanceof Error ? error.message : 'Der Livestream konnte nicht gestartet werden.'
      );
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      setPreviousVolume(volume);
      setVolume(0);
    } else {
      setVolume(previousVolume || DEFAULT_VOLUME);
    }
  };

  const liveElapsed = useMemo(() => {
    const anchor = playbackAnchorRef.current;
    if (!anchor) return nowPlaying?.nowPlaying.elapsed || 0;

    const deltaMs = Math.max(0, clock - anchor.syncedAtMs);
    const estimatedServerNowMs = anchor.serverTimeAtSyncMs + deltaMs;
    const elapsed = anchor.startedAt
      ? Math.max(0, estimatedServerNowMs / 1000 - anchor.startedAt)
      : anchor.elapsedAtSync + deltaMs / 1000;

    return anchor.duration > 0 ? Math.min(anchor.duration, elapsed) : elapsed;
  }, [clock, nowPlaying]);

  const progress = useMemo(() => {
    if (!nowPlaying || nowPlaying.nowPlaying.duration <= 0) return 0;
    return Math.min(100, Math.max(0, (liveElapsed / nowPlaying.nowPlaying.duration) * 100));
  }, [liveElapsed, nowPlaying]);

  const currentVersion = versionToken(
    nowPlaying?.nowPlaying.trackId,
    nowPlaying?.nowPlaying.startedAt,
    nowPlaying?.nowPlaying.title,
    nowPlaying?.nowPlaying.artist
  );
  const nextVersion = versionToken(
    nowPlaying?.nextPlaying?.title,
    nowPlaying?.nextPlaying?.artist
  );
  const coverUrl = nowPlaying?.nowPlaying.artUrl
    ? `/api/art/current?v=${currentVersion}`
    : null;
  const nextCoverUrl = nowPlaying?.nextPlaying?.artUrl
    ? `/api/art/next?v=${nextVersion}`
    : null;

  const volumeIcon =
    volume === 0 ? (
      <VolumeX size={20} />
    ) : volume < 0.5 ? (
      <Volume1 size={20} />
    ) : (
      <Volume2 size={20} />
    );

  return (
    <main
      ref={shellRef}
      className={`app-shell ${isPlaying ? 'is-playing' : ''}`}
      style={{
        '--cover-image': coverUrl ? `url("${coverUrl}")` : 'none'
      } as CSSProperties}
    >
      <div className="cover-background" aria-hidden="true">
        {coverUrl && (
          <img
            key={currentVersion}
            src={coverUrl}
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority="high"
            draggable={false}
          />
        )}
      </div>
      <div className="cover-background-overlay" aria-hidden="true" />
      <audio
        ref={audioRef}
        preload="none"
        crossOrigin="anonymous"
        onPlay={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false);
          resetVisualizer();
        }}
        onPlaying={() => {
          setIsBuffering(false);
          forceNormalPlaybackRate();
          startVisualizer();
        }}
        onCanPlay={() => setIsBuffering(false)}
        onWaiting={() => setIsBuffering(true)}
        onStalled={() => setIsBuffering(true)}
        onRateChange={forceNormalPlaybackRate}
        onError={() => {
          streamReadyRef.current = false;
          setIsPlaying(false);
          setIsBuffering(false);
          resetVisualizer();
        }}
      />


      <section className="content-grid">
        <article className="player-card glass-card">
          <div className="cover-column">
            <div className="cover-stage">
              <div className="cover-wrap">
                {coverUrl ? (
                  <img src={coverUrl} alt="Cover des aktuellen Titels" loading="eager" decoding="async" fetchPriority="high" draggable={false} />
                ) : (
                  <div className="cover-placeholder">
                    <Radio size={66} />
                  </div>
                )}
              </div>
            </div>

            <div className={`visualizer ${isPlaying ? 'active' : ''}`} aria-label="Musikvisualisierung">
              {Array.from({ length: VISUALIZER_BARS }, (_, index) => (
                <span
                  key={index}
                  ref={(element) => {
                    visualizerRefs.current[index] = element;
                  }}
                  style={{
                    '--bar-index': index,
                    '--bar-distance': Math.abs(index - (VISUALIZER_BARS - 1) / 2),
                    '--bar-color': visualizerBarColor(index)
                  } as CSSProperties}
                />
              ))}
            </div>
          </div>

          <div className="track-column">
            <div className="track-copy" key={currentVersion || 'loading'}>
              <div className="track-eyebrow">JETZT LÄUFT</div>
              <h1 ref={titleRef}>{currentTitle}</h1>
              <p className="artist">
                {nowPlaying?.nowPlaying.artist || 'Brovenco Radio'}
              </p>
            </div>

            <div className="live-meta-strip station-facts" aria-label="Senderstatistiken">
              <span title="Aktuelle Sendequelle">
                <RadioTower size={15} />
                {nowPlaying?.live.isLive
                  ? nowPlaying.live.streamerName || 'Live-DJ'
                  : 'AutoDJ'}
              </span>
              <span title="Aktuelle Hörer">
                <Headphones size={15} />
                {formatCount(nowPlaying?.listeners)} Hörer
              </span>
              <span title="Eingerichtete DJs">
                <UsersRound size={15} />
                {formatCount(nowPlaying?.library.djs)} DJs
              </span>
              <span title="Vorhandene Playlisten">
                <ListMusic size={15} />
                {formatCount(nowPlaying?.library.playlists)} Playlisten
              </span>
              <span title="Titel in der Musikbibliothek">
                <Disc3 size={15} />
                {formatCount(nowPlaying?.library.songs)} Lieder
              </span>
              <span title="Radiosender im AzuraCast-System">
                <Radio size={15} />
                {formatCount(nowPlaying?.library.stations)} Radiosender
              </span>
            </div>

            <div className="progress-block">
              <div className="progress-track">
                <span style={{ width: `${progress}%` }} />
              </div>
              <div className="progress-times">
                <span>{formatTime(liveElapsed)}</span>
                <span>{formatTime(nowPlaying?.nowPlaying.duration || 0)}</span>
              </div>
            </div>

            <div className="controls single-control">
              <button
                type="button"
                className="round-button primary"
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Radio pausieren' : 'Radio starten'}
                title={isPlaying ? 'Radio pausieren' : 'Radio starten'}
              >
                {isBuffering ? (
                  <LoaderCircle className="spin" size={31} />
                ) : isPlaying ? (
                  <Pause size={32} fill="currentColor" />
                ) : (
                  <Play size={32} fill="currentColor" />
                )}
              </button>
            </div>

            <div className="volume-row">
              <button
                type="button"
                className="plain-button"
                onClick={toggleMute}
                aria-label="Ton ein- oder ausschalten"
              >
                {volumeIcon}
              </button>
              <input
                aria-label="Lautstärke"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                style={{ '--volume': `${volume * 100}%` } as CSSProperties}
                onChange={(event) => setVolume(Number(event.target.value))}
              />
              <span>{Math.round(volume * 100)}%</span>
            </div>

            {streamError && (
              <div className="stream-error">
                <Info size={15} /> {streamError}
              </div>
            )}
          </div>
        </article>

        <aside className="side-column">
          <section className="next-card glass-card">
              <div className="section-title">
              <div>
                <span>DANACH</span>
                <strong>Nächster Titel</strong>
              </div>
              <div className="section-icon next-icon">
                <SkipForward size={18} />
              </div>
            </div>

            <div className="next-track" key={nextVersion || 'next-loading'}>
              {nextCoverUrl ? (
                <img src={nextCoverUrl} alt="Cover des nächsten Titels" loading="eager" decoding="async" draggable={false} />
              ) : (
                <div className="mini-cover">
                  <Radio size={24} />
                </div>
              )}
              <div className="next-track-copy">
                <strong className={compactTitleClass(nowPlaying?.nextPlaying?.title)}>{nowPlaying?.nextPlaying?.title || 'Wird gleich bekannt gegeben'}</strong>
                <span className="next-artist">{nowPlaying?.nextPlaying?.artist || 'Unbekannter Interpret'}</span>
              </div>
              <div className="track-time-inline next-time-inline" aria-label="Geplante Sendezeit">
                <span title="Startzeit">
                  <CirclePlay size={13} aria-hidden="true" />
                  <time dateTime={nowPlaying?.nextPlaying?.startedAt ? new Date(nowPlaying.nextPlaying.startedAt * 1000).toISOString() : undefined}>
                    {formatTimestamp(nowPlaying?.nextPlaying?.startedAt || null)}
                  </time>
                </span>
                <span title="Endzeit">
                  <CircleStop size={13} aria-hidden="true" />
                  <time dateTime={nowPlaying?.nextPlaying?.endsAt ? new Date(nowPlaying.nextPlaying.endsAt * 1000).toISOString() : undefined}>
                    {formatTimestamp(nowPlaying?.nextPlaying?.endsAt || null)}
                  </time>
                </span>
              </div>
            </div>
          </section>

          <section className="history-card glass-card">
              <div className="section-title">
              <div>
                <span>LETZTE TITEL</span>
                <strong>Zuletzt gespielt</strong>
              </div>
              <div className="section-icon history-icon">
                <History size={18} />
              </div>
            </div>

            <div className="history-list">
              {(nowPlaying?.history || []).slice(0, 5).map((entry, index) => {
                const historyCoverUrl = entry.artUrl
                  ? `/api/art/history/${index}?v=${versionToken(entry.playedAt, entry.title, entry.artist)}`
                  : null;

                return (
                  <article className="history-item" key={`${entry.playedAt}-${entry.title}-${index}`}>
                    <span className="history-number">{String(index + 1).padStart(2, '0')}</span>
                    {historyCoverUrl ? (
                      <img src={historyCoverUrl} alt="" loading="lazy" decoding="async" draggable={false} />
                    ) : (
                      <div className="history-cover-placeholder">
                        <Radio size={18} />
                      </div>
                    )}
                    <div className="history-copy">
                      <strong className={compactTitleClass(entry.title)}>{entry.title}</strong>
                      <span>{entry.artist}</span>
                    </div>
                    <div className="track-time-inline history-time-inline" aria-label="Sendezeit">
                      <span title="Startzeit">
                        <CirclePlay size={12} aria-hidden="true" />
                        <time dateTime={entry.playedAt ? new Date(entry.playedAt * 1000).toISOString() : undefined}>
                          {formatTimestamp(entry.playedAt)}
                        </time>
                      </span>
                      <span title="Endzeit">
                        <CircleStop size={12} aria-hidden="true" />
                        <time dateTime={entry.endsAt ? new Date(entry.endsAt * 1000).toISOString() : undefined}>
                          {formatTimestamp(entry.endsAt)}
                        </time>
                      </span>
                    </div>
                  </article>
                );
              })}

              {!nowPlaying?.history?.length && (
                <div className="history-empty">
                  <LoaderCircle className="spin" size={20} />
                  Verlauf wird live geladen …
                </div>
              )}
            </div>
          </section>
        </aside>
      </section>
    </main>
  );
}
